import { apiClient } from './client';
import type { CasaDigitalCombo } from '@/types/casa-digital.types';

// Calculation types for auto-calculated quantities
export type CalculationType = 'fixed' | 'per_guest' | 'per_table';

export interface CalculationParams {
  per_table_guests?: number;
}

export interface TemplateMenuItemSelection {
  menu_item_id: string;
  quantity: number;
  use_guest_count?: boolean; // Legacy
  calculation_type?: CalculationType;
  calculation_params?: CalculationParams;
}

export interface TemplateServiceSelection {
  service_id: string;
  quantity: number;
  use_guest_count?: boolean; // Legacy
  calculation_type?: CalculationType;
  calculation_params?: CalculationParams;
}

export interface Template {
  _id: string;
  name: string;
  type: 'weekday' | 'weekend';
  description?: string;
  selected_locations?: {
    location_id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addons?: Record<string, any>;
  }[];
  selected_menu_items?: TemplateMenuItemSelection[];
  selected_services?: TemplateServiceSelection[];
  service_percentage?: {
    percentage: number;
    selectedVolume: number;
    totalServices: number;
  } | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Legacy timestamp fields (for backward compatibility with old documents)
  createdAt?: string;
  updatedAt?: string;
  guest_management_enabled?: boolean;
  digital_invitations_enabled?: boolean;
  // Offered to clients under "Probar otro plan"
  show_as_alternative?: boolean;
  pitch?: string;
}

export interface CreateTemplateDto {
  name: string;
  type: 'weekday' | 'weekend';
  description?: string;
  selected_locations?: {
    location_id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addons?: Record<string, any>;
  }[];
  selected_menu_items?: TemplateMenuItemSelection[];
  selected_services?: TemplateServiceSelection[];
  is_active?: boolean;
  guest_management_enabled?: boolean;
  digital_invitations_enabled?: boolean;
  show_as_alternative?: boolean;
  pitch?: string;
}

export interface TemplateBudgetSimulation {
  guestCount: number;
  templateType: 'weekday' | 'weekend';
  locationTotal: number;
  locations: Array<{
    location_id: string;
    name: string;
    type: string;
    basePrice: number;
    tierInfo?: {
      tierName: string;
      tierDiscount: number | string;
      staticBasePrice: number;
      tierBasedPrice: number;
      discountApplied: number;
    };
    addons: {
      cleaning?: { quantity: number; price: number; total: number };
      extraGuests?: { quantity: number; pricePerGuest: number; total: number };
      pets?: { quantity: number; price: number; total: number };
      extraHours?: { quantity: number; pricePerHour: number; total: number };
      extraDays?: { quantity: number; pricePerDay: number; total: number };
      lodging?: { nights: number; pricePerNight: number; total: number };
      freeLodgingNight?: { nights: number; pricePerNight: number; discount: number; lodgingBaseGuests: number };
    };
    addonsTotal: number;
    total: number;
  }>;
  foodTotal: number;
  menuItems: Array<{
    menu_item_id: string;
    name: string;
    category: string;
    subcategory: string;
    is_bundle_pricing: boolean;
    units_per_person?: number;
    pricePerPerson: number;
    quantity: number;
    useGuestCount: boolean;
    calculation: string;
    total: number;
  }>;
  servicesTotal: number;
  servicesByCategory: Array<{
    category: string;
    categoryLabel: string;
    subtotal: number;
    services: Array<{
      service_id: string;
      name: string;
      subcategory: string;
      unit: string;
      pricePerUnit: number;
      quantity: number;
      hours?: number; // Hours multiplier for staff services
      useGuestCount: boolean;
      countsTowardDiscount: boolean;
      serviceWeight: number;
      calculation: string;
      total: number;
    }>;
  }>;
  casaDigitalTotal: number;
  casaDigitalModules: Array<{
    name: string;
    price: number;
    enabled: boolean;
  }>;
  // Combo pricing: null when not both modules enabled or no combo configured.
  // When applied, casaDigitalTotal already equals the combo price.
  casaDigitalCombo?: CasaDigitalCombo | null;
  servicePercentage: {
    percentage: number;
    selectedVolume: number;
    totalServices: number;
    serviceWeightsSum: number;
    familyGroupCountsSum: number;
    calculation: string;
  };
  discountTier: {
    tierName?: string;
    percentage: number;
    discount: number;
    label: string;
    color: string;
    lodgingIncluded?: boolean;
  };
  discountAmount: number;
  subtotal: number;
  iva: number;
  ivaRate: number;
  total: number;
  securityDepositRequired: boolean;
  securityDepositAmount: number;
  depositThreshold: number;
  calculationSteps: Array<{
    step: number;
    name: string;
    description: string;
    formula: string;
    result: number;
  }>;
}

export interface PublicPlanPrices {
  guests: number;
  weekday: number | null;
  weekend: number | null;
}

export const templatesApi = {
  create: (data: CreateTemplateDto) => apiClient.post<Template>('/templates', data),

  getAll: (params?: { type?: 'weekday' | 'weekend'; is_active?: boolean }) =>
    apiClient.get<Template[]>('/templates', { params }),

  getOne: (id: string) => apiClient.get<Template>(`/templates/${id}`),

  getForDate: (date: string) =>
    apiClient.get<Template>('/templates/for-date', { params: { date } }),

  /** Public: what each plan's quote comes to for a guest count (null when a plan has no active template) */
  getPublicPlanPrices: (guests: number) =>
    apiClient.get<PublicPlanPrices>('/templates/public/plan-prices', { params: { guests } }),

  update: (id: string, data: Partial<CreateTemplateDto>) =>
    apiClient.patch<Template>(`/templates/${id}`, data),

  toggleActive: (id: string) => apiClient.patch<Template>(`/templates/${id}/toggle-active`),

  delete: (id: string) => apiClient.delete(`/templates/${id}`),

  simulateBudget: (id: string, guestCount: number) =>
    apiClient.post<TemplateBudgetSimulation>(`/templates/${id}/simulate-budget`, {
      guest_count: guestCount,
    }),

  getServicePercentage: (id: string) =>
    apiClient.get<{
      percentage: number;
      selectedVolume: number;
      totalServices: number;
      serviceWeightsSum: number;
      familyGroupCountsSum: number;
      calculation: string;
    }>(`/templates/${id}/service-percentage`),
};

// ========== VOLUME IMPACT PREVIEW ==========
// Live, non-persisting preview of how service-weight / family-group changes
// (or a hypothetical not-yet-created service) would shift each active template's
// service-volume percentage and discount tier. Backend: POST
// /templates/volume-impact-preview (super_admin + manager_admin).

export interface VolumeWeightChange {
  service_id: string;
  service_weight: number;
}

export interface VolumeFamilyGroupChange {
  category_id: string;
  subcategory_id: string;
  family_group_count: number;
}

export interface VolumeHypotheticalService {
  category_id?: string;
  subcategory_id?: string;
  service_weight: number;
  counts_toward_discount: boolean;
  is_active: boolean;
}

export interface VolumeImpactPreviewRequest {
  weight_changes?: VolumeWeightChange[];
  family_group_changes?: VolumeFamilyGroupChange[];
  hypothetical_service?: VolumeHypotheticalService;
}

export interface VolumeTierBand {
  threshold_min: number;
  threshold_max: number;
  label: string;
}

export interface VolumeImpactTemplateRow {
  template_id: string;
  name: string;
  plan_type: 'weekday' | 'weekend';
  current: { percentage: number; tier: VolumeTierBand | null };
  proposed: { percentage: number; tier: VolumeTierBand | null };
  delta: number;
  tier_changed: boolean;
  is_active: boolean;
}

export interface VolumeImpactPreviewResponse {
  templates: VolumeImpactTemplateRow[];
  summary: {
    templates_affected: number;
    tiers_changed: number;
    inactive_templates_affected: number;
    denominator: { current: number; proposed: number };
  };
}

// Preview volume impact. The endpoint responds 201 (NestJS @Post); axios
// resolves any 2xx, so both 200 and 201 are treated as success. `token` is
// optional — the shared apiClient interceptor injects the stored token, but a
// caller may pass one explicitly.
export const previewVolumeImpact = async (
  payload: VolumeImpactPreviewRequest,
  token?: string,
): Promise<VolumeImpactPreviewResponse> => {
  const response = await apiClient.post<VolumeImpactPreviewResponse>(
    '/templates/volume-impact-preview',
    payload,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
  );
  return response.data;
};
